import { Injectable } from '@angular/core';
import { registerPlugin } from '@capacitor/core';
import { PlatformService } from './platform.service';
import { RestService } from './rest.service';
import { SessionService } from './session.service';
import { ErrorReportingService } from './error-reporting.service';
import { BrandService } from '../../services/brand-config.service';

export type StorePlan = 'onetime' | 'monthly' | 'annual';

export interface StoreProduct {
  id: string;
  title: string;
  description: string;
  /** Localized price string from the store, e.g. "₪14.90". */
  price: string;
  priceAmount: number;
  currency: string;
  type: 'subs' | 'inapp' | 'nonrenewing';
  /** ISO-8601 period for subscriptions (P1M, P1Y). */
  period?: string;
}

/** Signed purchase data handed to the server for verification. */
interface StorePurchase {
  platform: 'ios' | 'android';
  productId: string;
  transactionId?: string;
  state?: 'purchased' | 'pending' | 'unknown';
  signedTransaction?: string;
  purchaseToken?: string;
  originalJson?: string;
  signature?: string;
}

interface StoreBillingPlugin {
  getProducts(opts: { ids: string[] }): Promise<{ products: StoreProduct[] }>;
  purchase(opts: { id: string; accountToken?: string; accountId?: string }): Promise<StorePurchase>;
  restore(opts: { sync?: boolean }): Promise<{ purchases: StorePurchase[] }>;
  finish(opts: { transactionId?: string; purchaseToken?: string; consumable?: boolean }): Promise<void>;
  manageSubscriptions(opts: { productId?: string }): Promise<void>;
  addListener(event: 'purchaseUpdated', cb: (p: StorePurchase) => void): Promise<{ remove: () => void }>;
}

const StoreBilling = registerPlugin<StoreBillingPlugin>('StoreBilling');

export type StoreBuyResult = 'paid' | 'cancelled' | 'pending' | 'failed' | 'other_account';

/**
 * Native app-store billing (App Store / Google Play) for the "full picture"
 * plans. Inside the native apps the stores' own billing must be used for
 * digital access, so the paywall routes purchases here instead of the web
 * checkout. The server verifies every purchase and grants the same paid window
 * the web flow grants (User.paidUntil), so entitlement checks stay unchanged.
 */
@Injectable({ providedIn: 'root' })
export class StoreBillingService {
  private products: Partial<Record<StorePlan, StoreProduct>> | null = null;
  private listening = false;

  constructor(
    private platform: PlatformService,
    private brand: BrandService,
    private rest: RestService,
    private session: SessionService,
    private reporter: ErrorReportingService,
  ) {}

  /** Store billing is used in the native Peritale app (Markable keeps its current flow for now). */
  get enabled(): boolean {
    return this.platform.isNative && !this.brand.config.isMarkable;
  }

  get isIOS(): boolean {
    return this.platform.isIOS;
  }

  productId(plan: StorePlan): string {
    const prefix = this.brand.config.isMarkable ? 'markable' : 'peritale';
    const suffix = plan === 'onetime' ? 'pass_30d' : plan === 'monthly' ? 'sub_monthly' : 'sub_annual';
    return `${prefix}_${suffix}`;
  }

  private planFor(productId: string): StorePlan | null {
    for (const plan of ['onetime', 'monthly', 'annual'] as StorePlan[]) {
      if (this.productId(plan) === productId) return plan;
    }
    return null;
  }

  /** Store products with localized prices. Cached for the session. */
  async loadProducts(): Promise<Partial<Record<StorePlan, StoreProduct>>> {
    if (this.products) return this.products;
    const plans: StorePlan[] = ['onetime', 'monthly', 'annual'];
    const { products } = await StoreBilling.getProducts({ ids: plans.map((p) => this.productId(p)) });
    const out: Partial<Record<StorePlan, StoreProduct>> = {};
    for (const p of products || []) {
      const plan = this.planFor(p.id);
      if (plan) out[plan] = p;
    }
    if (Object.keys(out).length) this.products = out;
    return out;
  }

  /** Server check of one purchase, then finish it in the store. */
  private async verifyAndFinish(p: StorePurchase): Promise<StoreBuyResult> {
    if (p.state === 'pending') return 'pending';
    let resp: any;
    try {
      resp = await this.rest.request<any>({
        path: 'payment/store/verify',
        method: 'POST',
        body: {
          platform: p.platform,
          productId: p.productId,
          signedTransaction: p.signedTransaction,
          purchaseToken: p.purchaseToken,
          originalJson: p.originalJson,
          signature: p.signature,
        },
        throwError: true,
      });
    } catch (e: any) {
      this.reporter.report(e, 'store-billing.verify');
      if (e?.status === 409) {
        // Already linked to another account: finishing is still right (the
        // store purchase itself is valid), access stays with that account.
        await this.finish(p).catch(() => undefined);
        return 'other_account';
      }
      // Not finished: the store keeps it and the next restore/launch retries.
      return 'failed';
    }
    await this.finish(p).catch((e) => this.reporter.report(e, 'store-billing.finish'));
    if (resp?.paid) {
      await this.session.refreshUser();
      return 'paid';
    }
    return 'failed';
  }

  private finish(p: StorePurchase): Promise<void> {
    return p.platform === 'ios'
      ? StoreBilling.finish({ transactionId: p.transactionId })
      : StoreBilling.finish({
          purchaseToken: p.purchaseToken,
          consumable: this.planFor(p.productId) === 'onetime',
        });
  }

  async buy(plan: StorePlan): Promise<StoreBuyResult> {
    this.listenForUpdates();
    this.reporter.logEvent('store.purchase.start', { plan });
    let purchase: StorePurchase;
    try {
      purchase = await StoreBilling.purchase({ id: this.productId(plan) });
    } catch (e: any) {
      const code = e?.code || '';
      if (code === 'cancelled') return 'cancelled';
      if (code === 'pending') return 'pending';
      this.reporter.report(e, 'store-billing.purchase');
      return 'failed';
    }
    const result = await this.verifyAndFinish(purchase);
    this.reporter.logEvent('store.purchase.done', { plan, result });
    return result;
  }

  /**
   * Re-link every purchase the store account holds to this user. sync=true is
   * the explicit "Restore purchases" button (may ask the user to sign in to the
   * store); false is the quiet launch sync.
   */
  async restore(sync: boolean): Promise<boolean> {
    return (await this.restoreDetailed(sync)) === 'restored';
  }

  /** Same as restore(), but tells a refused purchase (linked to another account) apart from none found. */
  async restoreDetailed(sync: boolean): Promise<'restored' | 'none' | 'otherAccount'> {
    if (!this.enabled) return 'none';
    this.listenForUpdates();
    let purchases: StorePurchase[] = [];
    try {
      purchases = (await StoreBilling.restore({ sync })).purchases || [];
    } catch (e) {
      this.reporter.report(e, 'store-billing.restore');
      return 'none';
    }
    const valid = purchases.filter((p) => p.state !== 'pending' && this.planFor(p.productId));
    if (!valid.length) return 'none';
    try {
      const resp = await this.rest.request<any>({
        path: 'payment/store/sync',
        method: 'POST',
        body: {
          purchases: valid.map((p) => ({
            platform: p.platform,
            productId: p.productId,
            signedTransaction: p.signedTransaction,
            purchaseToken: p.purchaseToken,
            originalJson: p.originalJson,
            signature: p.signature,
          })),
        },
        throwError: true,
      });
      const results: any[] = resp?.results || [];
      await Promise.all(
        valid.map((p, i) =>
          results[i]?.reason === 'invalid' ? Promise.resolve() : this.finish(p).catch(() => undefined),
        ),
      );
      if (resp?.paid) await this.session.refreshUser();
      if (results.some((r) => r?.reason === 'other_account') && !results.some((r) => r?.paid)) return 'otherAccount';
      return resp?.paid ? 'restored' : 'none';
    } catch (e) {
      this.reporter.report(e, 'store-billing.sync');
      return 'none';
    }
  }

  manageSubscriptions(): Promise<void> {
    const plan = this.session.session?.user?.paidPlan;
    return StoreBilling.manageSubscriptions({
      productId: plan === 'monthly' || plan === 'annual' ? this.productId(plan) : undefined,
    });
  }

  /** Purchases that complete later (Ask to Buy, pending payments) get verified when they land. */
  private listenForUpdates(): void {
    if (this.listening || !this.enabled) return;
    this.listening = true;
    void StoreBilling.addListener('purchaseUpdated', (p) => {
      if (p?.state === 'purchased' && this.planFor(p.productId)) void this.verifyAndFinish(p);
    });
  }
}
